import { create } from "zustand";
import { AI_TOOLS, executeTool } from "../services/aiTools";
import { createChatTracer, isLangfuseEnabled } from "../services/langfuse";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system" | "tool";
  content: string;
  timestamp: number;
  isLoading?: boolean;
  toolCallId?: string;
  toolName?: string;
}

interface OpenAIMessage {
  role: "user" | "assistant" | "system" | "tool";
  content: string | null;
  tool_calls?: Array<{
    id: string;
    type: "function";
    function: { name: string; arguments: string };
  }>;
  tool_call_id?: string;
}

interface AiChatState {
  messages: ChatMessage[];
  isLoading: boolean;
  apiKey: string;
  model: string;
  systemPrompt: string;
  addMessage: (message: Omit<ChatMessage, "id" | "timestamp">) => string;
  updateMessage: (id: string, content: string) => void;
  removeMessage: (id: string) => void;
  setLoading: (loading: boolean) => void;
  setApiKey: (key: string) => void;
  setModel: (model: string) => void;
  clearMessages: () => void;
  sendMessage: (content: string) => Promise<void>;
}

const STORAGE_KEY = "ai-api-key";
const MODEL_KEY = "ai-model";

const SYSTEM_PROMPT = `You are an intelligent fleet management assistant for this platform.
You have DIRECT ACCESS to the fleet database through tools. ALWAYS use your tools to fetch real data when users ask about:
- Vehicles, units, or fleet information → use get_vehicles or get_vehicle_by_plate
- GPS devices or trackers → use get_devices
- Drivers → use get_drivers
- Alerts (speed, geofence, maintenance) → use get_alerts
- Trips → use get_trips
- Geofences → use get_geofences
- Fleet overview or summary → use get_fleet_summary

IMPORTANT RULES:
1. NEVER say you don't have access to data. You DO have access through your tools.
2. When asked about fleet data, ALWAYS call the appropriate tool first.
3. Present the data in a clear, organized way (use tables, lists, or summaries as appropriate).
4. Provide actionable insights when possible.
5. Respond in the same language the user writes to you.
6. Be concise and professional.`;

function generateId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

export const useAiChatStore = create<AiChatState>((set, get) => ({
  messages: [],
  isLoading: false,
  apiKey: localStorage.getItem(STORAGE_KEY) || "",
  model: localStorage.getItem(MODEL_KEY) || "gpt-4o-mini",
  systemPrompt: SYSTEM_PROMPT,

  addMessage: (message) => {
    const id = generateId();
    const newMessage: ChatMessage = {
      ...message,
      id,
      timestamp: Date.now(),
    };
    set((state) => ({ messages: [...state.messages, newMessage] }));
    return id;
  },

  updateMessage: (id, content) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, content, isLoading: false } : msg
      ),
    }));
  },

  removeMessage: (id) => {
    set((state) => ({
      messages: state.messages.filter((msg) => msg.id !== id),
    }));
  },

  setLoading: (loading) => set({ isLoading: loading }),

  setApiKey: (key) => {
    localStorage.setItem(STORAGE_KEY, key);
    set({ apiKey: key });
  },

  setModel: (model) => {
    localStorage.setItem(MODEL_KEY, model);
    set({ model });
  },

  clearMessages: () => set({ messages: [] }),

  sendMessage: async (content: string) => {
    const { apiKey, model, messages, systemPrompt, addMessage, updateMessage, removeMessage } = get();

    if (!apiKey) {
      addMessage({
        role: "assistant",
        content: "Please configure your OpenAI API key in the settings panel to start chatting.",
      });
      return;
    }

    addMessage({ role: "user", content });
    set({ isLoading: true });

    const assistantId = addMessage({
      role: "assistant",
      content: "",
      isLoading: true,
    });

    // Initialize Langfuse tracer if enabled
    const sessionId = `chat_${Date.now()}`;
    const tracer = isLangfuseEnabled() ? createChatTracer({ sessionId }) : null;

    try {
      // Build conversation history for OpenAI
      const conversationHistory: OpenAIMessage[] = [
        { role: "system", content: systemPrompt },
        ...messages
          .filter((m) => m.role !== "system" && m.role !== "tool")
          .map((m) => ({ role: m.role as "user" | "assistant", content: m.content })),
        { role: "user", content },
      ];

      let currentMessages = [...conversationHistory];
      let finalResponse = "";
      let iterationCount = 0;
      const maxIterations = 5; // Prevent infinite loops

      // Function calling loop
      while (iterationCount < maxIterations) {
        iterationCount++;
        const generationStartTime = new Date();

        const response = await fetch("https://api.openai.com/v1/chat/completions", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
          body: JSON.stringify({
            model,
            messages: currentMessages,
            tools: AI_TOOLS,
            tool_choice: "auto",
            temperature: 0.7,
            max_tokens: 4096,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = (errorData as { error?: { message?: string } })?.error?.message || response.statusText;
          throw new Error(`OpenAI API error: ${errorMessage}`);
        }

        const data = await response.json() as {
          choices: Array<{
            message: {
              content: string | null;
              tool_calls?: Array<{
                id: string;
                type: "function";
                function: { name: string; arguments: string };
              }>;
            };
            finish_reason: string;
          }>;
        };

        const assistantMessage = data.choices[0]?.message;

        // Log generation to Langfuse
        tracer?.generation({
          name: `chat-completion-${iterationCount}`,
          model,
          input: currentMessages,
          output: assistantMessage,
          startTime: generationStartTime,
          endTime: new Date(),
          metadata: {
            hasToolCalls: !!assistantMessage?.tool_calls?.length,
            finishReason: data.choices[0]?.finish_reason,
          },
        });

        // If the model wants to call tools
        if (assistantMessage?.tool_calls && assistantMessage.tool_calls.length > 0) {
          // Add assistant message with tool calls to conversation
          currentMessages.push({
            role: "assistant",
            content: assistantMessage.content,
            tool_calls: assistantMessage.tool_calls,
          });

          // Execute each tool call
          for (const toolCall of assistantMessage.tool_calls) {
            const toolName = toolCall.function.name;
            let toolArgs: Record<string, unknown> = {};
            
            try {
              toolArgs = JSON.parse(toolCall.function.arguments);
            } catch {
              toolArgs = {};
            }

            // Execute the tool
            const toolStartTime = new Date();
            const toolResult = await executeTool(toolName, toolArgs);

            // Log tool execution to Langfuse
            tracer?.span({
              name: `tool-${toolName}`,
              input: toolArgs,
              output: toolResult,
              startTime: toolStartTime,
              endTime: new Date(),
              metadata: { success: toolResult.success },
            });

            // Add tool result to conversation
            currentMessages.push({
              role: "tool",
              content: JSON.stringify(toolResult),
              tool_call_id: toolCall.id,
            });
          }

          // Continue the loop to get the final response
          continue;
        }

        // No more tool calls, we have the final response
        finalResponse = assistantMessage?.content || "No response received.";
        break;
      }

      updateMessage(assistantId, finalResponse);

      // Flush Langfuse traces
      tracer?.flush().catch(() => {});
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "An unexpected error occurred.";
      removeMessage(assistantId);
      addMessage({
        role: "assistant",
        content: `Error: ${errorMsg}`,
      });

      // Log error to Langfuse
      tracer?.event("error", { message: errorMsg });
      tracer?.flush().catch(() => {});
    } finally {
      set({ isLoading: false });
    }
  },
}));
